import React, { useLayoutEffect, useRef, useState } from 'react';
import { Typography } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import type { InterviewQuestion } from '@/constants/questions';
import styles from './QuestionCard.module.scss';

const { Title } = Typography;

interface QuestionCardProps {
  question: InterviewQuestion;
  questionIndex: number;
  clamped?: boolean;
}

/**
 * A substituted question's note, shown below the question text. Renders
 * nothing when `!questionChanged`, matching pre-substitution behavior.
 */
const ChangedQuestionNote: React.FC<{ question: InterviewQuestion }> = ({ question }) => {
  if (!question.questionChanged) return null;
  return (
    <div className={styles.changedQuestionNote}>
      This is a different question from your last attempt.
    </div>
  );
};

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  questionIndex,
  clamped = false,
}) => {
  const titleRef = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    if (!clamped) {
      setIsOverflowing(false);
      return;
    }
    const el = titleRef.current;
    if (!el) return;

    el.scrollTop = 0;
    const checkOverflow = () => setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [clamped, question.text]);

  const questionTitle = clamped ? (
    <div className={`${styles.clampWrap} ${isOverflowing ? styles.clampWrapOverflowing : ''}`}>
      <Title
        level={3}
        ref={titleRef}
        className={`${styles.questionText} ${styles.questionTextClamped}`}
      >
        {question.text}
      </Title>
    </div>
  ) : (
    <Title level={3} className={styles.questionText}>
      {question.text}
    </Title>
  );

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={questionIndex}
        className={styles.container}
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -12, opacity: 0 }}
        transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
      >
        {questionTitle}
        <ChangedQuestionNote question={question} />
      </motion.div>
    </AnimatePresence>
  );
};
